import { AddOnAccountBillTo, LineItemAccordionDetails } from "components/Common/LineItemGrid/LineItemGridStyles";
import { Fragment, RefObject, useEffect } from "react";
import { models } from "types/api/viewModels";
import AccountAndBillTo from "../../Common/AccountAndBillTo/AccountAndBillTo.tsx";
import { BuildNewOrderLineItemsHeader, UploadErrorWaringText, UploadErrorWarning } from "../NewOrders.styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
	GlobalAttributeEmptyError,
	LineNumber,
	NewOrderBuildGridDoorStyleColumnHeader,
	NewOrderBuildGridFinishColumnHeader,
	NewOrderBuildGridQuantityColumnHeader,
	NewOrderEdit,
	NewOrderRemoveButton,
	NewOrderRemoveGroup
} from "../constants";
import {
	formatFinishDescription,
	getAttributes,
	getConfigDisplay,
	getSubheaderAttributes
} from "../utils/NewOrderUtils";
import {
	AddItemButtonWrapper,
	BuildLineItemGrid,
	ComboColumn,
	ComboColumnSeparated,
	ConfigAttributeTools
} from "./buildNewOrderLineItems.styles";
import GlobalFormProvider from "../AddGlobalAttributes/forms/GlobalFormProvider";
import AddGlobalAttributes from "../AddGlobalAttributes/AddGlobalAttributes";
import { Button, Divider } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditLineItem from "../EditLineItem/EditLineItem";
import Header from "../../Common/LineItemGrid/Header/Header.tsx";
import { LinkButton } from "components/Common/Link";
import { Modification } from "components/Common/LineItemGrid";
import { AddItemButton } from "../AddLineItem/AddLineItemStyles";
import AddIcon from "@mui/icons-material/Add";
import { AddLineAddLineButtonText } from "../AddLineItem/constants";
import AddNewLineItem from "../AddLineItem/AddLineItem.tsx";
import { FileContentProps } from "./FileContent.tsx";
import { lineItemIdSymbol } from "features/api/newOrderApi.ts";
import { AddColorText, ColorOptionText, CustomText, VendorText } from "constants/text.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { useUserLocation } from "hooks/useUserLocation.ts";

interface ConfigurationContentProps extends FileContentProps {
	configuration: models["PendingOrderConfigurationViewModel"] | models["ValidatedOrderConfigurationViewModel"];
	setConfigurationToUpdate: (configuration: models["PendingOrderConfigurationViewModel"]) => void;
	configIndex: number;
	showEditLineItem: number | undefined;
	editLineItemIndex: number | undefined;
	toggleEditItem: (index: number) => void;
	selectedEditItem: RefObject<models["PendingLineItemViewModel"] | undefined>;
	handleEditLineItem: (item: models["PendingLineItemViewModel"], itemIndex: number, index: number) => void;
	showAddItemButton: [number, number] | undefined;
	toggleAddItem: (
		groupIndex: number | undefined,
		configIndex: number | undefined,
		shouldShowEstimateButton: boolean
	) => void;
	parsedCSV: models["ValidatedOrderViewModel"][] | undefined;
}

const ConfigurationContent = ({
	configuration,
	isAddOn,
	isLoading,
	postError,
	setConfigurationToUpdate,
	handleConfigurationUpdate,
	configIndex,
	handleRemoveGroup,
	searchValue,
	showEditLineItem,
	editLineItemIndex,
	group,
	toggleEditItem,
	selectedEditItem,
	handleMoveLineItem,
	lineItemErrors,
	handleEditLineItem,
	handleOpenRemoveModal,
	groupIndex,
	showAddItemButton,
	toggleAddItem,
	parsedCSV,
	handleLineItem,
	groupedDisplayConfigs,
	setLineItemErrorAsEdited
}: ConfigurationContentProps) => {
	const { isCustomColorPage: isCustomColor } = useUserLocation();
	const dispatch = useDispatch();

	useEffect(() => {
		const lastConfigIndex = group.configurations.length - 1;
		if (
			group.configurations[lastConfigIndex]?.configurationId === configuration.configurationId &&
			(!group.configurations[lastConfigIndex]?.lineItems ||
				group.configurations[lastConfigIndex]?.lineItems?.length === 0)
		)
			toggleAddItem(groupIndex, lastConfigIndex, false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const pageName = isAddOn ? "add-ons" : "new-order";

	const getAllImportErrors = (item: models["PendingLineItemViewModel"]): string[] => {
		const allModErrors =
			item.modifications
				?.map((mod) =>
					mod.importError ? `${LineNumber}${mod.lineItemNumber} - ${mod.importError.message}` : undefined
				)
				.filter(Boolean) ?? [];

		return [item.importError?.message, ...allModErrors].filter(Boolean) as string[];
	};

	return (
		<Fragment key={configuration.configurationId}>
			{isAddOn && !isLoading && (
				<AddOnAccountBillTo>
					<AccountAndBillTo addOnConfig={configuration} />
				</AddOnAccountBillTo>
			)}
			<LineItemAccordionDetails>
				{postError && configuration.lineItems?.length === 0 && (
					<UploadErrorWarning>
						<ErrorOutlineIcon aria-label="add-line-items" />
						<UploadErrorWaringText data-testid="empty-attribute-group">
							{GlobalAttributeEmptyError}
						</UploadErrorWaringText>
					</UploadErrorWarning>
				)}
				<BuildNewOrderLineItemsHeader>
					<div data-testid="new-order-build-label">
						<div>{getAttributes(configuration, isCustomColor)}</div>
						<div>{getSubheaderAttributes(configuration)}</div>
					</div>
					<ConfigAttributeTools>
						<GlobalFormProvider isUpdate>
							<AddGlobalAttributes
								setConfigurationToUpdate={() => setConfigurationToUpdate(configuration)}
								handleConfigurationUpdate={(config) => handleConfigurationUpdate(config)}
								isUpdate
								isCustomColor={isCustomColor}
							/>
						</GlobalFormProvider>
						<Button
							onClick={() => handleRemoveGroup(configuration)}
							variant="text"
							data-testid="remove-group"
							sx={{ gap: "8px" }}
							data-id={`${pageName}-build-order-remove-global-attributes-button`}
						>
							<DeleteIcon />
							{NewOrderRemoveGroup}
						</Button>
					</ConfigAttributeTools>
				</BuildNewOrderLineItemsHeader>
				{/* eslint-disable-next-line sonarjs/cognitive-complexity */}
				{configuration?.lineItems?.map((item: models["PendingLineItemViewModel"], itemIndex) => {
					const foundSearch = `${item.sku} - ${item.description}`
						.toUpperCase()
						.includes((searchValue as string).toUpperCase());
					// Check if this is the item being edited
					const isEditing = configIndex === showEditLineItem && itemIndex === editLineItemIndex;
					if (foundSearch) {
						const error =
							item[lineItemIdSymbol] && lineItemErrors[item[lineItemIdSymbol]]
								? lineItemErrors[item[lineItemIdSymbol]]
								: getAllImportErrors(item);
						if (isEditing) {
							// Render EditLineItem instead of labels
							return (
								<EditLineItem
									key={`${item.sku}-${item.lineItemNumber}-edit`}
									group={group}
									configuration={configuration}
									addEditVisibility={toggleEditItem}
									selectedEditItem={selectedEditItem}
									description={configuration?.globals?.productLine?.description}
									editLineItemIndex={editLineItemIndex}
									handleMoveLineItem={handleMoveLineItem}
									isAddLineItem={false}
									error={error}
									setLineItemErrorAsEdited={setLineItemErrorAsEdited}
									lineItemId={item[lineItemIdSymbol] ?? ""}
									isEditing={isEditing}
								/>
							);
						} else {
							// Render the regular view with labels
							return (
								<BuildLineItemGrid
									isLoading={isLoading}
									key={`${item.sku}-${item.lineItemNumber}`}
									error={error}
									mainRowContent={[
										<Header
											key={`${item.sku}-${item.lineItemNumber}-header`}
											item={item}
											isEditing={isEditing}
										/>,
										<div key={`${item.sku}-${item.lineItemNumber}-empty`} />,
										isCustomColor ? (
											<div key={`${item.sku}-${item.lineItemNumber}-vendor`}>
												<div>{VendorText}</div>
												<div data-testid="line-item-grid-data-vendor">
													{configuration.globals?.finish?.customColor?.vendorDescription}
												</div>
											</div>
										) : (
											<div key={`${item.sku}-${item.lineItemNumber}-door-style`}>
												<div>{NewOrderBuildGridDoorStyleColumnHeader}</div>
												<div data-testid="line-item-grid-data-door-style">
													{configuration.globals?.style?.description}
												</div>
											</div>
										),
										isCustomColor ? (
											<div key={`${item.sku}-${item.lineItemNumber}-colorOption`}>
												<div>{ColorOptionText}</div>
												<div data-testid="line-item-grid-data-colorOption">
													{[
														configuration.globals?.finish?.customColor?.colorCode ===
														CustomText
															? undefined
															: configuration.globals?.finish?.customColor?.colorCode,
														configuration.globals?.finish?.customColor?.colorDescription
													]
														.filter(Boolean)
														.join(" - ")}
												</div>
											</div>
										) : (
											<div key={`${item.sku}-${item.lineItemNumber}-finish`}>
												<div>{NewOrderBuildGridFinishColumnHeader}</div>
												<div data-testid="line-item-grid-data-finish">
													{formatFinishDescription(
														configuration.globals?.finish,
														isCustomColor
													)}
												</div>
											</div>
										),
										<ComboColumn key={`${item.sku}-${item.lineItemNumber}-remove-combo`}>
											<div>
												<div>{NewOrderBuildGridQuantityColumnHeader}</div>
												<div data-testid="line-item-grid-data-quantity">
													{item.quantityOrdered}
												</div>
											</div>

											<div>
												<LinkButton
													onClick={() => {
														handleEditLineItem(item, itemIndex, configIndex);
													}}
													data-testid="line-item-edit-link"
													data-id={`${pageName}-build-order-edit-item-button`}
												>
													{NewOrderEdit}
												</LinkButton>

												<LinkButton
													variant="remove"
													onClick={() => handleOpenRemoveModal(item, itemIndex, configIndex)}
													data-testid="line-item-remove-link"
													data-id={`${pageName}-build-order-remove-item-button`}
												>
													{NewOrderRemoveButton}
												</LinkButton>
											</div>
										</ComboColumn>,
										<ComboColumnSeparated key={`${item.sku}-${item.lineItemNumber}-qty-separated`}>
											<div>{NewOrderBuildGridQuantityColumnHeader}</div>
											<div data-testid="line-item-grid-data-quantity">{item.quantityOrdered}</div>
										</ComboColumnSeparated>,
										<ComboColumnSeparated
											key={`${item.sku}-${item.lineItemNumber}-remove-separated`}
										>
											<LinkButton
												onClick={() => {
													handleEditLineItem(item, itemIndex, configIndex);
												}}
												data-testid="line-item-edit-link"
												data-id={`${pageName}-build-order-edit-item-button`}
											>
												{NewOrderEdit}
											</LinkButton>

											<LinkButton
												variant="remove"
												onClick={() => handleOpenRemoveModal(item, itemIndex, configIndex)}
												data-testid="line-item-remove-link"
												data-id={`${pageName}-build-order-remove-item-button`}
											>
												{NewOrderRemoveButton}
											</LinkButton>
										</ComboColumnSeparated>
									]}
									modificationRows={item.modifications?.map((modification) => (
										<Modification
											description={modification.description}
											key={`${modification.lineItemNumber} ${modification.sku}`}
											lineNumber={modification.lineItemNumber}
											title={modification.sku}
											values={modification.values}
										/>
									))}
									dimensionRows={item.requiredDimensions?.map((dimension) => (
										<Modification
											dimensionDescription={dimension.description}
											key={dimension.description}
											dimensionValue={dimension.value}
											isUnrestrictedDimension={dimension.isUnrestrictedValue}
											lineNumber={undefined}
										/>
									))}
									requiredCategoriesRows={item.requiredCategories?.map((category) => (
										<Modification
											key={category.categoryName}
											requiredCategory={category}
											lineNumber={undefined}
										/>
									))}
								/>
							);
						}
					} else {
						return <></>;
					}
				})}
			</LineItemAccordionDetails>
			{(groupIndex !== showAddItemButton?.[0] || configIndex !== showAddItemButton?.[1]) &&
			showEditLineItem === undefined ? (
				<>
					<AddItemButton
						variant="text"
						onClick={() => {
							if (isCustomColor && configuration.lineItems && configuration.lineItems.length > 0) {
								dispatch(
									newOrderActions.addDuplicateConfiguration({
										configuration: configuration,
										oldConfigId: configuration.configurationId,
										newConfigId: uuidv4()
									})
								);
							} else {
								toggleAddItem(groupIndex, configIndex, false);
							}
						}}
						data-testid="add-item-button"
						data-id={`${pageName}-build-order-add-item-button`}
					>
						<AddIcon />
						{isCustomColor ? AddColorText : AddLineAddLineButtonText}
					</AddItemButton>
					<AddItemButtonWrapper>{getConfigDisplay(configuration, parsedCSV)}</AddItemButtonWrapper>
				</>
			) : (
				(groupIndex === showAddItemButton?.[0] || configIndex === showAddItemButton?.[1]) &&
				showEditLineItem === undefined && (
					<>
						<AddNewLineItem
							selectedEditItem={selectedEditItem}
							handleLineItem={handleLineItem}
							configuration={configuration}
							addItemVisibility={() => toggleAddItem(undefined, undefined, true)}
							isAddLineItem
						/>
						<AddItemButtonWrapper>{getConfigDisplay(configuration, parsedCSV)}</AddItemButtonWrapper>
					</>
				)
			)}
			{configIndex !== (groupedDisplayConfigs?.[groupIndex]?.configurations?.length ?? 0) - 1 && (
				<Divider sx={{ margin: "16px", padding: 0 }} />
			)}
		</Fragment>
	);
};

export default ConfigurationContent;
